import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import PublicNav from '@/components/PublicNav';
import WorldMap from '@/components/giraffe/WorldMap';
import CountryList from '@/components/giraffe/CountryList';
import ZLoadingBar from '@/components/shared/ZLoadingBar';
import { Breadcrumb, BreadcrumbList, BreadcrumbItem, BreadcrumbLink, BreadcrumbPage, BreadcrumbSeparator } from '@/components/ui/breadcrumb';
import Logotype from '@/components/shared/Logotype';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TravelsCountries() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = 'Countries Visited · Werner du Toit';
    axios.get(`${API}/public/giraffe/countries`)
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const visitedCodes = useMemo(() => {
    if (!data) return new Set();
    const codes = new Set();
    data.continents.forEach(c => c.countries.forEach(cc => codes.add(cc.code)));
    return codes;
  }, [data]);

  const visitCounts = useMemo(() => {
    if (!data) return {};
    const counts = {};
    data.continents.forEach(c => c.countries.forEach(cc => { counts[cc.code] = cc.visits; }));
    return counts;
  }, [data]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0f]">
        <ZLoadingBar accent="#10B981" loading />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center">
        <p className="text-sm font-mono text-[#454860]">Not available.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0f] pt-4 md:pt-16 pb-20">
      <PublicNav />

      <div className="px-6 md:px-12 max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mt-6 mb-10">
          <div>
            <p className="text-xs tracking-[0.4em] uppercase mb-2"
              style={{ fontFamily: "'Cormorant Garamond', serif", color: '#b0b4c0' }}>
              The World So Far
            </p>
            <h1 className="text-4xl sm:text-5xl font-black tracking-tight"
              style={{ fontFamily: "'Playfair Display', serif", color: '#f0f0f5', lineHeight: 1 }}>
              COUNTRIES VISITED
            </h1>
          </div>
          <Breadcrumb>
            <BreadcrumbList style={{ gap: 0 }}>
              <BreadcrumbItem>
                <BreadcrumbLink onClick={() => navigate('/travels')} style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center' }}>
                  <Logotype app="giraffe" size="sidebar" />
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator style={{ padding: '0 8px', color: '#454860' }} />
              <BreadcrumbItem>
                <BreadcrumbPage style={{ color: '#f0f0f5', fontSize: 13, fontWeight: 400 }}>
                  Countries
                </BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </div>

        {/* Map */}
        <div style={{ background: 'rgba(255,255,255,0.03)', border: '0.5px solid rgba(255,255,255,0.06)', borderRadius: 14, overflow: 'hidden', position: 'relative' }}>
          <div style={{ transform: 'scale(1.18) translateY(-6%)', transformOrigin: '50% 42%' }}>
            <WorldMap visitedCodes={visitedCodes} visitCounts={visitCounts} />
          </div>
          <div style={{ position: 'absolute', bottom: 18, right: 22, pointerEvents: 'none' }}>
            <span style={{ fontFamily: "'Playfair Display', serif", fontSize: 36, fontWeight: 900, color: '#10B981', opacity: 0.85, textShadow: '0 2px 16px rgba(0,0,0,0.8)' }}>
              {visitedCodes.size}
            </span>
          </div>
        </div>

        {/* Country list — read-only (no onEdit) */}
        <CountryList continents={data.continents} />
      </div>

      <div className="text-center py-12">
        <p className="text-xs tracking-[0.3em]" style={{ fontFamily: "'Cormorant Garamond', serif", color: 'rgba(255,255,255,0.06)' }}>
          WERNER LABS
        </p>
      </div>
    </div>
  );
}
